/**
 * What the trainer's assignment list says about ONE plan template — the two facts any surface
 * standing over a plan has to know before it offers to end it or to delete it.
 *
 * `GET /plan-assignment` returns every assignment the trainer has, flat, and a template appears in
 * it in two completely different roles:
 *
 * · as the assignment's OWN template (`planTemplateId`) — this plan IS somebody's running plan,
 *   which is what a fork is;
 * · as the template an assignment was FORKED FROM (`sourceTemplateId`) — this plan is a library
 *   entry other people's plans were cut from.
 *
 * They are never the same row and they answer different questions, so both are resolved in one pass
 * over the list rather than by two `find`s that would each walk it again.
 *
 * ── The fork is the LIVE row, and only then the first one ────────────────────────────────────
 * A template can appear as a fork more than once — assigned, completed and re-assigned to the same
 * client. `planForkCustomer` takes the first match and is right to: every such row names the same
 * customer, so which one it reads cannot change the answer.
 *
 * That justification does NOT carry to an assignment id or a status, and taking it was a defect.
 * The rows come back in the server's own order, which this slice does not control; if the finished
 * row came first, a plan the client is training today would read as terminal and the surface would
 * offer no ending at all. So a live row — Active or Paused, `liveAssignmentStatus`'s rule — wins over
 * every other, and the first match is the fallback for a template whose only rows are history. That
 * fallback still matters: it is what makes an already-ended plan answer "over" rather than "absent".
 *
 * ── The follower count is LIVE only, and it is `liveAssignmentStatus`'s rule ─────────────────
 * Active and Paused, matching the population the library's own `assignedClientCount` counts, so a
 * sentence written from this number and the figure on the plans list cannot disagree. A completed
 * or cancelled assignment is history; deleting the template it was cut from takes nothing from
 * anyone who is training today.
 *
 * ── A CANCELLED fork resolves to a fork all the same ─────────────────────────────────────────
 * `PlanAssignmentProcessor` filters `Cancelled` out of this list server-side, so a plan ended early
 * simply is not here and answers `fork: null` — the known blind spot `planForkCustomer` records at
 * length. Nothing is done about it here: a surface that gets `null` offers no ending, which is the
 * right outcome for a plan that has already ended, arrived at for the wrong reason.
 *
 * No React, no DOM, no query hook, no i18next.
 */

import type { TrainWithMeModulesTrainingDtosResponsesPlanAssignmentSummary as PlanAssignmentSummary } from '~/shared/api/generated/model/trainWithMeModulesTrainingDtosResponsesPlanAssignmentSummary';

import { liveAssignmentStatus } from './assignmentStatus';

/** The assignment a fork template IS, reduced to what a lifecycle decision needs. */
export interface PlanFork {
  readonly assignmentId: number;
  /** The wire's status, untranslated. `null` where the summary carried none. */
  readonly status: string | null;
  /**
   * Whose plan this is. Not for display — an act taken on this assignment has to invalidate the
   * reads keyed on the CLIENT (`GET /plan-assignment/customer/{id}`), and that key shares no prefix
   * with the list this row came out of. `null` where the summary named nobody.
   */
  readonly customerId: number | null;
  /**
   * Whether the client still has plan ahead of them — the ONE fact `planEnding` turns on, answered
   * here from the summary because a surface outside the calendar has no grid to read extents off.
   *
   * Two modes, two readings of one question, and the split is the roll-up's own:
   *
   * · **Dated** — `nextWorkoutDate` is the earliest scheduled date among the not-yet-finished
   *   workouts, so it is non-null for a plan whose only unfinished row is a SKIP behind the client.
   *   The question is whether there is anything AHEAD, so the date is compared to today rather than
   *   merely tested for absence. Testing absence is what made this disagree with the calendar.
   * · **Self-paced** — `nextWorkoutDate` is null for every one of them by construction (the roll-up
   *   gates it, because an undated plan's earliest unfinished row is the oldest skip). The counts
   *   are the cursor: `WorkoutCount` on such a plan is the number of occurrences the client has
   *   LIVED, so "done < total" is "the cursor has somewhere to go".
   *
   * An absent or zero total is not evidence of a finished plan and reads as "still ahead": of the
   * two endings, that is the one that does not tell a trainer their client's plan is over.
   */
  readonly hasPlanLeft: boolean;
}

export interface PlanTemplateAssignments {
  /** The assignment this template is opened as, or `null` for a library entry. */
  readonly fork: PlanFork | null;
  /** How many LIVE assignments were forked from this template. */
  readonly followerCount: number;
}

export const NO_TEMPLATE_ASSIGNMENTS: PlanTemplateAssignments = { fork: null, followerCount: 0 };

/** The wire's spelling of the one undated mode. Matched exactly, like every other status here. */
const SELF_PACED = 'SelfPaced';

function hasPlanLeft(assignment: PlanAssignmentSummary, now: number): boolean {
  if ((assignment.schedulingMode ?? '').trim() === SELF_PACED) {
    const total = assignment.workoutCount ?? 0;
    return total === 0 || (assignment.completedWorkoutCount ?? 0) < total;
  }

  const next = assignment.nextWorkoutDate;
  if (next == null || next === '') return false;
  const at = Date.parse(next);
  // A date the wire sent in a shape this build cannot read is not evidence that a plan is over.
  return Number.isNaN(at) ? true : at >= now;
}

function toFork(assignment: PlanAssignmentSummary, now: number): PlanFork | null {
  if (assignment.id == null) return null;
  return {
    assignmentId: assignment.id,
    status: assignment.status ?? null,
    customerId: assignment.customerId ?? null,
    hasPlanLeft: hasPlanLeft(assignment, now),
  };
}

/**
 * One pass over the trainer's assignments, for one template.
 *
 * @param planTemplateId the plan open on the surface asking.
 * @param now the clock, in epoch milliseconds — a parameter, so this module stays pure and the
 *   "ahead of today" boundary is testable without one.
 */
export function planTemplateAssignments(
  assignments: readonly PlanAssignmentSummary[],
  planTemplateId: number,
  now: number,
): PlanTemplateAssignments {
  let live: PlanFork | null = null;
  let first: PlanFork | null = null;
  let followerCount = 0;

  for (const assignment of assignments) {
    if (assignment.planTemplateId === planTemplateId) {
      const isLive = liveAssignmentStatus(assignment.status) !== null;
      if (isLive && live === null) live = toFork(assignment, now);
      if (!isLive && first === null) first = toFork(assignment, now);
    }
    if (
      assignment.sourceTemplateId === planTemplateId &&
      liveAssignmentStatus(assignment.status) !== null
    ) {
      followerCount += 1;
    }
  }

  return { fork: live ?? first, followerCount };
}
